import { css, cx } from '@emotion/css';
import { useController, useFormContext } from 'react-hook-form';
import { Checkmark } from '../../shared/assets/icons';
import { Colors } from '../../shared/styles/colors';
import { labelStyle, p2Style } from '../../shared/styles/text';
import { Link } from 'react-router-dom';
import { appRoutes } from '../../routing/app-routes';
import { Motion } from '../../shared/styles/tokens';

interface TermsConfirmationProps {
  name: string;
}

export const TermsConfirmation = (
  props: TermsConfirmationProps,
): JSX.Element => {
  const { control } = useFormContext();
  const { name } = props;
  const {
    field,
    fieldState: { error },
  } = useController({
    name,
    control,
  });

  return (
    <div className={checkboxContainerStyle}>
      <label>
        <input
          className={hiddenCheckbox}
          type="checkbox"
          {...field}
          checked={field.value}
          aria-invalid={error ? true : undefined}
        />
        <span className={checkboxStyle} id={'checkbox'} aria-hidden="true">
          <Checkmark className={checkMarkStyle} />
        </span>
        <span className={checkboxTextStyle}>
          I accept Terms of Use and Privacy Policy.
          <span className={requiredMarkStyle}>*</span>
        </span>
      </label>
      <p className={linksStyle}>
        Read the <Link to={appRoutes.termsOfUse}>Terms of Use</Link> and the{' '}
        <Link to={appRoutes.privacyPolicy}>Privacy Policy</Link>.
      </p>
      {error && (
        <span className={cx(labelStyle, errorStyle)} role="alert">
          {error.message}
        </span>
      )}
    </div>
  );
};

const checkboxContainerStyle = css`
  margin-bottom: 1.5rem;
  position: relative;
  > label {
    display: flex;
    align-items: flex-start;
    cursor: pointer;
    > input:checked ~ #checkbox {
      background-color: ${Colors.CAPRI_BLUE};
      border-color: ${Colors.CAPRI_BLUE};
    }
    > input:focus-visible ~ #checkbox {
      outline: 2px solid ${Colors.CAPRI_BLUE};
      outline-offset: 2px;
    }
  }
`;

const checkboxStyle = css`
  flex: 0 0 auto;
  width: 22px;
  height: 22px;
  border: 2px solid ${Colors.BORDER_STRONG};
  border-radius: 6px;
  margin-right: 0.75rem;
  margin-top: 0.0625rem;
  display: flex;
  justify-content: space-around;
  background: ${Colors.WHITE};
  transition:
    background-color ${Motion.fast},
    border-color ${Motion.fast};
  > svg {
    align-self: center;
  }
`;

const hiddenCheckbox = css`
  position: absolute;
  opacity: 0;
  cursor: pointer;
  height: 0;
  width: 0;
`;

const checkMarkStyle = css``;

const checkboxTextStyle = css`
  ${p2Style}
  -webkit-user-select: none; /* Chrome all / Safari all */
  -moz-user-select: none; /* Firefox all */
  -ms-user-select: none; /* IE 10+ */
  user-select: none;
`;

const requiredMarkStyle = css`
  color: ${Colors.ERROR_DARK};
  margin-left: 0.125rem;
`;

const linksStyle = css`
  ${p2Style}
  color: ${Colors.TEXT_SECONDARY};
  margin-top: 0.5rem;
  padding-left: calc(22px + 0.75rem);

  a {
    color: ${Colors.CAPRI_BLUE};
    text-decoration: underline;
    text-underline-offset: 0.2em;
    cursor: pointer;
  }
`;

const errorStyle = css`
  display: block;
  color: ${Colors.ERROR_DARK};
  margin-top: 0.5rem;
  padding-left: calc(22px + 0.75rem);
  &:first-letter {
    text-transform: capitalize;
  }
`;
